import { EventSourcePolyfill } from 'event-source-polyfill';
import type { SortingState } from '@tanstack/react-table';
import { EnvVariables } from 'src/shared/env-variables';
import { TokenManager } from 'src/shared/helpers';
import { ProcessingStagesValue } from './enums';
import { ProcessingStatus } from 'src/shared/enums';
import { FinalMergingStates } from './constants';
import { isEmpty } from 'lodash';

export const getSseEventSource = (idList: string[], endpoint: string) => {
  const sseUrl = EnvVariables.logServerUrl + endpoint + idList.join();
  // Creating a new sse connection.
  const authHeader = {
    Authorization: `Bearer ${TokenManager.getToken()}`,
  };
  const progressEventSource = new EventSourcePolyfill(sseUrl, {
    headers: authHeader,
  });
  return progressEventSource;
};

export const getProcessingStageNumber = (stage: string) => {
  if (stage === ProcessingStagesValue.AlignPhotos) {
    return 1;
  } else if (stage === ProcessingStagesValue.GeneratePointCloud) {
    return 2;
  } else if (stage === ProcessingStagesValue.GenerateOrthomosaic) {
    return 3;
  }
  return null;
};

export const isProgressStatusTransitionValid = (
  prevStatus: ProcessingStatus,
  nextStatus: ProcessingStatus,
) => {
  const isPreviousStateFinal = FinalMergingStates.includes(prevStatus);
  const isNextStateFinal = FinalMergingStates.includes(nextStatus);
  if (isPreviousStateFinal && !isNextStateFinal) {
    return false;
  }
  if (
    prevStatus === ProcessingStatus.Processing &&
    nextStatus === ProcessingStatus.Pending
  ) {
    return false;
  }
  if (
    prevStatus === ProcessingStatus.Pending &&
    nextStatus === ProcessingStatus.Completed
  ) {
    return false;
  }
  return true;
};

/**
 * Converts TanStack Table sorting state to API sort_by parameter format.
 * Returns undefined if no sorting is applied.
 * Supports snake_case field name mapping.
 *
 * @param sorting - The sorting state from TanStack Table
 * @param fieldMapping - Optional mapping of column IDs to API field names
 * @returns The sort_by parameter string (e.g., "field_name" or "-field_name" for descending)
 */
export const convertTanStackSortingToApiParam = (
  sorting: SortingState,
  fieldMapping?: Record<string, string>,
): string | undefined => {
  if (isEmpty(sorting)) {
    return;
  }

  const { id, desc } = sorting[0];
  const apiField = fieldMapping?.[id] ?? id;

  return desc ? `-${apiField}` : apiField;
};
